/* eslint-disable css-modules/no-unused-class */
import classNames from 'classnames';
import { useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import variables from '@/components/modules/destination-facts-manual/destination-facts-manual-box/destination-facts-manual-box.variables';
import AnchorLink from '@/components/molecules/anchor-link/anchor-link';
import Button from '@/components/molecules/button/button';
import StarRating from '@/components/molecules/star-rating/star-rating';
import Link from '@/components/utilities/link/link';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './destination-facts-manual-box.module.scss';

export interface DestinationFactsManualBoxProps {
  /** The class name to apply */
  className?: string;
  /** The text to show as a headline */
  headline?: string;
  /** A list of items detailing the information */
  informationItems?: {
    leftHeadline?: string;
    leftText?: string;
    rightHeadline?: string;
    rightLink?: Content.Components['LinkDto'] | null;
    rightText?: string;
  }[];
  /** The link to show */
  link: Content.Components['LinkDto'] | null;
  /** A list of items detailing the ratings */
  ratingItems?: {
    rating?: number;
    text?: string;
  }[];
}

const DestinationFactsManualBox = ({
  className,
  informationItems = [],
  link,
  ratingItems = [],
  headline,
}: DestinationFactsManualBoxProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const isLaptop = useWindowSize('laptop');

  const headlineElement = (
    <Typography className={styles.headline} tagStyle="headingSmall">
      {headline}
    </Typography>
  );

  return (
    <div
      className={classNames(
        styles.component,
        className,
        variables?.settings?.theme && styles[variables.settings.theme],
        {
          [styles.isOpen]: isOpen,
        },
      )}
    >
      {/* The box is always open from laptop and up, so the header only toggles below that */}
      {isLaptop ? (
        <div className={styles.header}>{headlineElement}</div>
      ) : (
        <button
          aria-expanded={isOpen}
          className={styles.header}
          onClick={() => setIsOpen(!isOpen)}
          type="button"
        >
          {headlineElement}

          <Button
            className={styles.toggleButton}
            icon={isOpen ? 'ChevronUp' : 'ChevronDown'}
            renderAs="div"
            variant="tertiary"
          />
        </button>
      )}

      <div className={styles.body}>
        {ratingItems.map((item, itemIndex) => (
          <div key={itemIndex} className={styles.section}>
            <div className={styles.sectionLeftSide}>
              <Typography tagStyle="bodySmall">{item.text}</Typography>
            </div>

            {item.rating && (
              <div className={styles.sectionRightSide}>
                <StarRating rating={item.rating} />
              </div>
            )}
          </div>
        ))}

        {informationItems.map((item, itemIndex) => (
          <div key={itemIndex} className={styles.section}>
            <div className={styles.sectionLeftSide}>
              {item.leftHeadline && (
                <Typography tagStyle="bodySmallBold">
                  {item.leftHeadline}
                </Typography>
              )}

              {item.leftText && (
                <Typography tagStyle="bodySmall">{item.leftText}</Typography>
              )}
            </div>

            <div className={styles.sectionRightSide}>
              {item.rightHeadline && (
                <Typography tagStyle="bodySmallBold">
                  {item.rightHeadline}
                </Typography>
              )}

              {item.rightText && (
                <Typography tagStyle="bodySmall">{item.rightText}</Typography>
              )}

              {item.rightLink && (
                <Link
                  className={styles.link}
                  target={item.rightLink.target}
                  url={item.rightLink.url}
                >
                  <Typography tagStyle="bodySmall">
                    {item.rightLink.text}
                  </Typography>
                </Link>
              )}
            </div>
          </div>
        ))}

        {link && (
          <div className={styles.section}>
            <AnchorLink icon="ArrowRight" {...link} />
          </div>
        )}
      </div>
    </div>
  );
};

DestinationFactsManualBox.displayName = 'DestinationFactsManualBox';

export default DestinationFactsManualBox;
